import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import Structures from './pages/Structures';
import Assets from './pages/Assets';
import Members from './pages/Members';
import EventPlanner from './pages/EventPlanner';
import Logs from './pages/Logs';
import MemberStats from './pages/MemberStats';
import LogVisualizer from './pages/LogVisualizer';
import { authService } from './services/authService';
import './App.css';

// EVE Online inspired dark theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#33b5e5', // EVE cyan/blue
      light: '#5ac8f5',
      dark: '#0099cc'
    },
    secondary: {
      main: '#00ccff', // Bright cyan accent
      light: '#33d6ff',
      dark: '#0099cc'
    },
    background: {
      default: '#0a0e14', // Deep space black
      paper: '#141b24' // Darker blue-black for cards
    },
    text: {
      primary: '#e8eaed', // Light gray for main text
      secondary: '#9ba4b0' // Muted gray for secondary text
    },
    error: {
      main: '#ff4444',
      light: '#ff6666',
      dark: '#cc0000'
    },
    warning: {
      main: '#ffaa00',
      light: '#ffbb33',
      dark: '#ff8800'
    },
    success: {
      main: '#00c851',
      light: '#33d97a',
      dark: '#009933'
    },
    divider: 'rgba(51, 181, 229, 0.12)' // Subtle cyan divider
  },
  typography: {
    fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", sans-serif',
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h5: {
      fontWeight: 600
    },
    h6: {
      fontWeight: 600
    },
    button: {
      fontWeight: 500,
      letterSpacing: '0.02em'
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#33b5e5 #141b24',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#141b24'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#33b5e5',
            borderRadius: '4px',
            '&:hover': {
              background: '#5ac8f5'
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 500,
          padding: '8px 20px'
        },
        contained: {
          boxShadow: '0 2px 8px rgba(51, 181, 229, 0.25)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(51, 181, 229, 0.35)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 8,
          border: '1px solid rgba(51, 181, 229, 0.1)'
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.6)'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(to bottom, #141b24 0%, #0f1419 100%)',
          borderRight: '1px solid rgba(51, 181, 229, 0.15)'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(51, 181, 229, 0.15)',
            borderLeft: '3px solid #33b5e5',
            '&:hover': {
              backgroundColor: 'rgba(51, 181, 229, 0.25)'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(51, 181, 229, 0.08)'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6
        }
      }
    }
  }
});

// Protected Route component
function ProtectedRoute({ children, user, loading }) {
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle logout - clear user state to trigger redirect to login
  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear user anyway to force redirect to login
      setUser(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route
            element={
              <ProtectedRoute user={user} loading={loading}>
                <Layout user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/events" element={<EventPlanner />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/structures" element={<Structures />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/members" element={<Members />} />
            <Route path="/member-stats" element={<MemberStats />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/log-visualizer" element={<LogVisualizer />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
